<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "~/components/global/WrIcon.vue";

interface CardItem {
  title: string;
  desc: string;
}

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    cards: CardItem[];
    svgIcons?: string[];
    backgroundImage?: string;
    ctaText?: string;
    ctaLink?: string | null;
    /** Override for the card title color (replaces the default `#501D71`/white theme colors). */
    titleClass?: string;
    /** Override for the card description color (replaces the default black/`#AF66E4` theme colors). */
    descClass?: string;
    /** Base color for the divider lines (rendered at `lineOpacity`%, like the default `#A550FF`). */
    lineColor?: string;
    /** Opacity of the divider lines, in percent (default 30). */
    lineOpacity?: number;
    /** Show pulsing wave circles at the desktop divider intersections. */
    waves?: boolean;
    /** Icon rendered in the center of each wave circle. */
    waveIcon?: string;
    /** Optional background applied to every card (empty = no background, as before). */
    cardBg?: string;
    /** Optional border-image gradient for every card (empty = no border, as before). */
    cardBorder?: string;
    /**
     * Per-card backgrounds, in card order (top row left→right, then bottom row).
     * A card with no entry falls back to `cardBg`.
     */
    cardBgs?: string[];
    /**
     * Apply the per-card background (`cardBgs`/`cardBg`) to the desktop cards.
     * When off, the desktop cards render transparent (mobile cards are unaffected).
     */
    showDesktopCardBg?: boolean;
    /**
     * Render the sub-lg (mobile/tablet) block as a 2-across grid at tablet
     * (`md`) instead of a single-column stack. Off by default.
     */
    tabletTwoCol?: boolean;
  }>(),
  {
    svgIcons: () => [],
    backgroundImage: "/images/Our_Mobile_App_Design_Process.svg",
    ctaText: "Start Your Project Today",
    ctaLink: null,
    titleClass: "",
    descClass: "",
    lineColor: "",
    lineOpacity: 30,
    waves: false,
    waveIcon: "streamline:star-1",
    cardBg: "",
    cardBorder: "",
    cardBgs: () => [],
    showDesktopCardBg: true,
    tabletTwoCol: false,
  },
);

const cardStyleFor = (index: number) => {
  const bg = props.showDesktopCardBg ? props.cardBgs[index] || props.cardBg : "";
  if (!bg && !props.cardBorder) return undefined;
  return {
    ...(bg ? { background: bg } : {}),
    // slice 1 so the gradient maps onto the 1px border
    ...(props.cardBorder
      ? { border: "1px solid", borderImage: `${props.cardBorder} 1` }
      : {}),
  };
};

// Mobile stack: one uniform background for every card (first non-empty entry),
// so the stacked cards don't alternate light/transparent.
const mobileCardBg = computed(() => props.cardBgs.find(Boolean) || props.cardBg || "");
const mobileCardStyle = computed(() =>
  mobileCardBg.value ? { backgroundImage: mobileCardBg.value } : undefined
);

const emit = defineEmits<{
  (e: "cta-clicked"): void;
}>();

const shouldShowRightBorder = (index: number) => [0, 1, 3].includes(index);
const shouldShowBottomBorder = (index: number) => index < 3;
const shouldShowLeftBorder = (index: number) => [2, 4].includes(index);
</script>

<template>
  <section
    class="my-16 lg:my-24 flex flex-col items-center text-center relative max-w-7xl mx-auto"
    :style="{ '--reasons-line-color': lineColor || undefined, '--reasons-line-opacity': lineOpacity + '%' }"
    aria-label="Reasons to work with us"
  >
    <!-- Background Image -->
    <div
      v-if="props.backgroundImage"
      class="absolute inset-0 hidden lg:flex justify-center items-start pt-[7.5rem] pointer-events-none z-0 overflow-hidden"
    ></div>

    <!-- Section Heading -->
    <h2
      class="text-2xl sm:text-3xl lg:text-5xl font-semibold !leading-[1.25] text-center relative z-10 font-avant-demi tracking-tight max-w-4xl bg-gradient-to-r from-[#12DBFF] to-[#BA55FE] bg-clip-text text-transparent"
    >
      {{ heading }}
    </h2>

    <!-- Subcopy Description -->
    <p
      class="text-center mt-4 text-base lg:text-lg font-normal tracking-wide relative z-10 font-avant max-w-2xl text-black dark:text-white opacity-80"
    >
      {{ subcopy }}
    </p>

    <!-- Cards Grid: 3 on top, 2 below -->
    <div class="w-full max-w-5xl mt-8 sm:mt-12 lg:mt-16 relative z-10">
      <!-- Desktop -->
      <div class="hidden lg:block">
        <!-- Top Row: 3 cards -->
        <div class="flex relative has-row-bottom-divider">
          <article
            v-for="(card, index) in cards.slice(0, 3)"
            :key="`top-${index}`"
            :style="cardStyleFor(index)"
            class="flex-1 p-8 flex flex-col items-center gap-2 text-center relative"
            :class="{
              'has-right-divider': index < 2,
            }"
          >
            <WrIcon
              v-if="props.svgIcons[index]"
              :name="props.svgIcons[index]"
              class="text-[#501D71] dark:text-white size-7 lg:size-8"
            />
            <h3
              class="text-base lg:text-xl font-medium font-avant"
              :class="titleClass || 'text-[#501D71] dark:text-white'"
            >
              {{ card.title }}
            </h3>
            <p
              class="text-xs lg:text-[15px] font-normal tracking-wide leading-relaxed font-avant"
              :class="descClass || 'text-black dark:text-[#AF66E4]'"
            >
              {{ card.desc }}
            </p>
          </article>
          <!-- Wave circles at the two vertical/horizontal divider intersections -->
          <div v-if="waves" aria-hidden="true" class="absolute inset-x-0 bottom-0 pointer-events-none">
            <span
              v-for="pos in ['33.333%', '66.667%']"
              :key="pos"
              class="reasons-wave"
              :style="{ left: pos }"
            >
              <WrIcon :name="waveIcon" class="size-3.5" />
            </span>
          </div>
        </div>

        <!-- Bottom Row: centered -->
        <div class="flex justify-center relative">
          <article
            v-for="(card, index) in cards.slice(3)"
            :key="`bottom-${index}`"
            :style="cardStyleFor(index + 3)"
            class="flex-1 p-8 flex flex-col items-center gap-2 text-center relative"
            :class="{
              'has-right-divider': index < cards.slice(3).length - 1,
            }"
          >
            <WrIcon
              v-if="props.svgIcons[index + 3]"
              :name="props.svgIcons?.[index + 3] ?? ''"
              class="text-[#501D71] dark:text-white max-sm:size-6 size-8"
            />
            <h3
              class="text-base lg:text-xl font-medium font-avant"
              :class="titleClass || 'text-[#501D71] dark:text-white'"
            >
              {{ card.title }}
            </h3>
            <p
              class="text-xs lg:text-[15px] font-normal tracking-wide leading-relaxed font-avant"
              :class="descClass || 'text-black dark:text-[#AF66E4]'"
            >
              {{ card.desc }}
            </p>
          </article>
        </div>
      </div>

      <!-- Mobile -->
      <div
        class="lg:hidden"
        :class="[
          tabletTwoCol ? 'grid grid-cols-1 md:grid-cols-2 items-start' : 'flex flex-col items-center',
          mobileCardBg ? 'gap-4' : 'gap-0',
        ]"
      >
        <article
          v-for="(card, index) in cards"
          :key="`mobile-${index}`"
          :style="mobileCardStyle"
          class="flex flex-col items-start gap-2 text-left w-full py-6 relative"
          :class="[
            mobileCardBg
              ? 'va-reasons-mobile-card rounded-2xl border border-[rgba(124,108,246,0.18)] px-5 dark:border-[rgba(96,128,224,0.2)]'
              : '',
            !mobileCardBg && index < cards.length - 1 ? 'has-bottom-divider' : '',
          ]"
        >
          <WrIcon
            v-if="props.svgIcons[index]"
            :name="props.svgIcons[index]"
            class="text-[#501D71] dark:text-white max-sm:size-6 size-8"
          />
          <h3
            class="text-base lg:text-xl font-medium font-avant text-left"
              :class="titleClass || 'text-[#501D71] dark:text-white'"
          >
            {{ card.title }}
          </h3>
          <p
            class="text-xs lg:text-[15px] font-normal tracking-wide leading-relaxed font-avant text-left"
            :class="descClass || 'text-black dark:text-[#AF66E4]'"
          >
            {{ card.desc }}
          </p>
        </article>
      </div>
    </div>

    <!-- CTA Button -->
    <div class="flex justify-center mt-8 lg:mt-16 relative z-10 w-full">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-10 lg:h-12 w-full sm:w-auto"
        append-class="text-sm lg:text-base font-normal px-6 sm:px-8 w-full sm:w-auto justify-center h-max-content"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>

<style scoped>
/* Gradient right divider — uses ::before */
.has-right-divider::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    color-mix(in srgb, var(--reasons-line-color, #A550FF) var(--reasons-line-opacity, 30%), transparent) 20%,
    color-mix(in srgb, var(--reasons-line-color, #A550FF) var(--reasons-line-opacity, 30%), transparent) 80%,
    transparent 100%
  );
  pointer-events: none;
}

/* Gradient bottom divider on the row container — uses ::after */
.has-row-bottom-divider::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent 0%,
    color-mix(in srgb, var(--reasons-line-color, #A550FF) var(--reasons-line-opacity, 30%), transparent) 10%,
    color-mix(in srgb, var(--reasons-line-color, #A550FF) var(--reasons-line-opacity, 30%), transparent) 90%,
    transparent 100%
  );
  pointer-events: none;
}

/* Wave circles at the desktop divider intersections: a small core dot with two
   rings that expand and fade, staggered half a cycle apart so the pulses read
   as continuous waves radiating from the point */
.reasons-wave {
  position: absolute;
  bottom: 0;
  transform: translate(-50%, 50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--reasons-line-color, #A550FF) 25%, transparent);
  border: 1px solid color-mix(in srgb, var(--reasons-line-color, #A550FF) 60%, transparent);
}
.reasons-wave::before,
.reasons-wave::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid color-mix(in srgb, var(--reasons-line-color, #A550FF) 60%, transparent);
  animation: reasons-wave-pulse 2.8s ease-out infinite;
}
.reasons-wave::after {
  animation-delay: 1.4s;
}
@keyframes reasons-wave-pulse {
  0% {
    transform: scale(1);
    opacity: 0.9;
  }
  100% {
    transform: scale(4);
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .reasons-wave::before,
  .reasons-wave::after {
    animation: none;
    opacity: 0;
  }
}

/* Mobile card base surface — gives the per-card gradient a visible card
   body so the stacked cards read as cards, not a flat text list */
.va-reasons-mobile-card {
  background-color: rgba(255, 255, 255, 0.55);
}

html.my-app-dark .va-reasons-mobile-card {
  background-color: rgba(15, 22, 58, 0.6);
}

/* Mobile bottom divider on cards */
.has-bottom-divider::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent 0%,
    color-mix(in srgb, var(--reasons-line-color, #A550FF) var(--reasons-line-opacity, 30%), transparent) 10%,
    color-mix(in srgb, var(--reasons-line-color, #A550FF) var(--reasons-line-opacity, 30%), transparent) 90%,
    transparent 100%
  );
  pointer-events: none;
}
</style>
